import React, {useState, useEffect} from 'react';
import axios from 'axios';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    Chip,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Search as SearchIcon,
    ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const TYPE_LABEL = {one_time: 'Achat unique', subscription: 'Abonnement'};

const STATUS_LABEL = {
    pending: 'En attente',
    paid: 'Payée',
    active: 'Active',
    canceled: 'Annulée',
    expired: 'Expirée',
    failed: 'Échouée',
};

const STATUS_COLOR = {
    pending: 'default',
    paid: 'success',
    active: 'success',
    canceled: 'default',
    expired: 'warning',
    failed: 'error',
};

export default function OrdersPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        const token = document.cookie.match(/AUTOMONE_ACCESS=([^;]+)/)?.[1];
        if (!token) {
            setLoading(false);
            return;
        }

        axios.get(`${API_URL}/api/orders`, {headers: {Authorization: `Bearer ${token}`}})
            .then(res => setOrders(res.data?.orders || []))
            .catch(err => {
                setError('Impossible de charger les commandes');
                console.error(err.message);
            })
            .finally(() => setLoading(false));
    }, []);

    const filteredOrders = orders.filter(order => {
        const matchesSearch = (order.application_name || order.application_client_id || '')
            .toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const stats = {
        total: orders.length,
        active: orders.filter(o => o.status === 'active' || o.status === 'paid').length,
        revenue: orders
            .filter(o => o.status === 'active' || o.status === 'paid')
            .reduce((sum, o) => sum + (Number(o.amount) || 0), 0) / 100,
    };

    return (
        <Box sx={{p: {xs: 2, sm: 3}}}>
            <Typography variant="h4" gutterBottom sx={{color: 'primary.main', fontWeight: 'bold'}}>
                Commandes de la boutique
            </Typography>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            <Grid container spacing={3} sx={{mb: 3}}>
                {[
                    {label: 'Total', value: stats.total, color: 'primary.main'},
                    {label: 'Actives / payées', value: stats.active, color: 'success.main'},
                    {label: 'Revenu (actives/payées)', value: `${stats.revenue.toFixed(2)} €`, color: 'info.main'},
                ].map(s => (
                    <Grid item xs={12} sm={4} key={s.label}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>{s.label}</Typography>
                                <Typography variant="h4" sx={{color: s.color}}>
                                    {loading ? '…' : s.value}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box sx={{mb: 3, display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 2, alignItems: {xs: 'stretch', sm: 'center'}}}>
                <TextField
                    placeholder="Rechercher une application..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        ),
                    }}
                    sx={{flexGrow: 1}}
                />
                <FormControl sx={{minWidth: {xs: '100%', sm: 160}}}>
                    <InputLabel>Statut</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        label="Statut"
                    >
                        <MenuItem value="all">Tous</MenuItem>
                        {Object.entries(STATUS_LABEL).map(([value, label]) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Paper sx={{overflowX: 'hidden', width: '100%'}}>
                <Box sx={{overflowX: 'auto', width: '100%'}}>
                    <TableContainer>
                        <Table size="small" sx={{minWidth: {xs: '100%', sm: '650px'}}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Application</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Statut</TableCell>
                                    <TableCell>Montant</TableCell>
                                    <TableCell>Créée le</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{py: 3}}>
                                            <CircularProgress size={24}/>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredOrders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Typography color="text.secondary">Aucune commande trouvée</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredOrders
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((order) => (
                                        <TableRow key={order.id} hover>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <ShoppingCartIcon sx={{mr: 1, color: 'primary.main', fontSize: 18}}/>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {order.application_name || order.application_client_id}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {TYPE_LABEL[order.type] || order.type}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={STATUS_LABEL[order.status] || order.status}
                                                    color={STATUS_COLOR[order.status] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {(Number(order.amount) / 100).toFixed(2)} {String(order.currency || '').toUpperCase()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : '—'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredOrders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
        </Box>
    );
}
